if (!customElements.get("pdp-reviews-carousel")) {
    class PdpReviewsCarousel extends HTMLElement {
        connectedCallback() {
            this.track = this.querySelector("[data-reviews-track]");
            this.prevButton = this.querySelector("[data-reviews-prev]");
            this.nextButton = this.querySelector("[data-reviews-next]");
            this.slides = Array.from(this.querySelectorAll("[data-reviews-slide]"));

            if (!this.track || this.slides.length === 0) return;

            this.autoplayEnabled = this.dataset.autoplay === "true";
            this.intervalMs = Number(this.dataset.interval) || 2000;
            this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

            this.pointerId = null;
            this.isDragging = false;
            this.hasMoved = false;
            this.startX = 0;
            this.startScroll = 0;
            this.autoplayTimer = null;
            this.autoplayRestartTimer = null;
            this.isHovering = false;
            this.userTouching = false;
            this.pendingTouchRestart = false;
            this.programmaticScroll = false;
            this.snapTimer = null;
            this.programmaticScrollTimer = null;

            this.onPointerDown = this.handlePointerDown.bind(this);
            this.onPointerMove = this.handlePointerMove.bind(this);
            this.onPointerUp = this.handlePointerUp.bind(this);
            this.onTouchPointerUp = this.handleTouchPointerUp.bind(this);
            this.onTrackScroll = this.handleTrackScroll.bind(this);
            this.onWheel = this.handleWheel.bind(this);
            this.onClickCapture = this.handleClickCapture.bind(this);
            this.onPrev = () => this.goTo(this.currentIndex() - 1);
            this.onNext = () => this.goTo(this.currentIndex() + 1);
            this.onEnter = () => {
                this.isHovering = true;
                this.stopAutoplay();
            };
            this.onLeave = () => {
                this.isHovering = false;
                this.startAutoplay();
            };
            this.onFocusIn = () => this.stopAutoplay();
            this.onFocusOut = (event) => {
                if (!this.contains(event.relatedTarget)) this.startAutoplay();
            };
            this.onVisibility = () => {
                if (document.hidden) this.stopAutoplay();
                else this.startAutoplay();
            };
            this.onResize = () => this.goTo(this.currentIndex(), false);
            this.onBlockSelect = this.handleBlockSelect.bind(this);
            this.onBlockDeselect = () => this.startAutoplay();

            this.track.addEventListener("pointerdown", this.onPointerDown);
            this.track.addEventListener("scroll", this.onTrackScroll, { passive: true });
            this.track.addEventListener("wheel", this.onWheel, { passive: true });
            this.track.addEventListener("click", this.onClickCapture, true);
            this.prevButton?.addEventListener("click", this.onPrev);
            this.nextButton?.addEventListener("click", this.onNext);
            this.addEventListener("mouseenter", this.onEnter);
            this.addEventListener("mouseleave", this.onLeave);
            this.addEventListener("focusin", this.onFocusIn);
            this.addEventListener("focusout", this.onFocusOut);
            document.addEventListener("visibilitychange", this.onVisibility);
            window.addEventListener("resize", this.onResize);
            document.addEventListener("shopify:block:select", this.onBlockSelect);
            document.addEventListener("shopify:block:deselect", this.onBlockDeselect);

            this.updateButtons();
            this.startAutoplay();
        }

        disconnectedCallback() {
            this.stopAutoplay();
            this.clearAutoplayRestart();
            this.clearSnapTimer();
            if (this.programmaticScrollTimer) window.clearTimeout(this.programmaticScrollTimer);
            this.track?.removeEventListener("pointerdown", this.onPointerDown);
            this.track?.removeEventListener("pointermove", this.onPointerMove);
            this.track?.removeEventListener("pointerup", this.onPointerUp);
            this.track?.removeEventListener("pointercancel", this.onPointerUp);
            this.track?.removeEventListener("scroll", this.onTrackScroll);
            this.track?.removeEventListener("wheel", this.onWheel);
            this.track?.removeEventListener("click", this.onClickCapture, true);
            window.removeEventListener("pointerup", this.onTouchPointerUp);
            window.removeEventListener("pointercancel", this.onTouchPointerUp);
            this.prevButton?.removeEventListener("click", this.onPrev);
            this.nextButton?.removeEventListener("click", this.onNext);
            this.removeEventListener("mouseenter", this.onEnter);
            this.removeEventListener("mouseleave", this.onLeave);
            this.removeEventListener("focusin", this.onFocusIn);
            this.removeEventListener("focusout", this.onFocusOut);
            document.removeEventListener("visibilitychange", this.onVisibility);
            window.removeEventListener("resize", this.onResize);
            document.removeEventListener("shopify:block:select", this.onBlockSelect);
            document.removeEventListener("shopify:block:deselect", this.onBlockDeselect);
        }

        visibleCount() {
            const width = window.innerWidth;
            if (width >= 990) return 3;
            if (width >= 750) return 2;
            return 1;
        }

        maxIndex() {
            return Math.max(0, this.slides.length - this.visibleCount());
        }

        slideStep() {
            const first = this.slides[0];
            if (!first) return 0;
            const styles = window.getComputedStyle(this.track);
            const gap = parseFloat(styles.columnGap || styles.gap) || 0;
            return Math.round(first.getBoundingClientRect().width + gap);
        }

        currentIndex() {
            const step = this.slideStep();
            if (!step) return 0;
            return Math.round(this.track.scrollLeft / step);
        }

        goTo(index, animate = true) {
            const max = this.maxIndex();
            let next = index;

            if (index > max) next = 0;
            else if (index < 0) next = max;

            const left = Math.round(next * this.slideStep());
            const useSmooth = animate && !this.reduceMotion;
            this.programmaticScroll = true;
            if (this.programmaticScrollTimer) window.clearTimeout(this.programmaticScrollTimer);

            this.track.style.scrollSnapType = "none";
            this.track.style.scrollBehavior = useSmooth ? "smooth" : "auto";
            this.track.scrollTo({ left, behavior: useSmooth ? "smooth" : "auto" });
            this.updateButtons();

            this.programmaticScrollTimer = window.setTimeout(
                () => {
                    if (Math.abs(this.track.scrollLeft - left) > 1) {
                        this.track.style.scrollBehavior = "auto";
                        this.track.scrollLeft = left;
                    }
                    this.track.style.scrollSnapType = "x mandatory";
                    this.track.style.scrollBehavior = "auto";
                    this.programmaticScroll = false;
                },
                useSmooth ? 500 : 50,
            );
        }

        snapToNearest(animate = true) {
            this.goTo(this.currentIndex(), animate);
        }

        clearSnapTimer() {
            if (this.snapTimer) {
                window.clearTimeout(this.snapTimer);
                this.snapTimer = null;
            }
        }

        scheduleSnapToNearest() {
            this.clearSnapTimer();
            this.snapTimer = window.setTimeout(() => {
                this.snapToNearest(false);
                this.scheduleAutoplayRestart();
            }, 140);
        }

        updateButtons() {
            const max = this.maxIndex();
            const hide = max === 0;
            this.prevButton?.classList.toggle("is-hidden", hide);
            this.nextButton?.classList.toggle("is-hidden", hide);
        }

        startAutoplay() {
            this.stopAutoplay();
            if (!this.autoplayEnabled || this.reduceMotion || this.slides.length <= this.visibleCount()) return;
            if (this.isHovering || this.isDragging || this.userTouching || document.hidden) return;

            this.autoplayTimer = window.setInterval(() => {
                this.goTo(this.currentIndex() + 1);
            }, this.intervalMs);
        }

        stopAutoplay() {
            if (this.autoplayTimer) {
                window.clearInterval(this.autoplayTimer);
                this.autoplayTimer = null;
            }
        }

        clearAutoplayRestart() {
            if (this.autoplayRestartTimer) {
                window.clearTimeout(this.autoplayRestartTimer);
                this.autoplayRestartTimer = null;
            }
        }

        scheduleAutoplayRestart() {
            this.clearAutoplayRestart();
            this.autoplayRestartTimer = window.setTimeout(() => {
                if (!this.userTouching && !this.isDragging) {
                    this.pendingTouchRestart = false;
                    this.startAutoplay();
                }
            }, 280);
        }

        handleWheel() {
            if (this.programmaticScroll) return;
            this.stopAutoplay();
            this.scheduleSnapToNearest();
        }

        handleTrackScroll() {
            if (this.programmaticScroll) return;
            this.stopAutoplay();
            this.scheduleSnapToNearest();
        }

        handleTouchPointerUp() {
            this.userTouching = false;
            this.pendingTouchRestart = true;
            window.removeEventListener("pointerup", this.onTouchPointerUp);
            window.removeEventListener("pointercancel", this.onTouchPointerUp);
            this.scheduleAutoplayRestart();
        }

        handlePointerDown(event) {
            if (event.pointerType === "touch" || event.pointerType === "pen") {
                this.userTouching = true;
                this.stopAutoplay();
                this.clearAutoplayRestart();
                window.addEventListener("pointerup", this.onTouchPointerUp);
                window.addEventListener("pointercancel", this.onTouchPointerUp);
                return;
            }
            if (event.button !== 0) return;

            this.pointerId = event.pointerId;
            this.isDragging = true;
            this.hasMoved = false;
            this.startX = event.clientX;
            this.startScroll = this.track.scrollLeft;
            this.stopAutoplay();

            this.track.classList.add("is-dragging");
            this.track.style.scrollBehavior = "auto";
            this.track.style.scrollSnapType = "none";
            this.track.setPointerCapture(event.pointerId);
            this.track.addEventListener("pointermove", this.onPointerMove);
            this.track.addEventListener("pointerup", this.onPointerUp);
            this.track.addEventListener("pointercancel", this.onPointerUp);
        }

        handlePointerMove(event) {
            if (!this.isDragging || event.pointerId !== this.pointerId) return;

            const delta = event.clientX - this.startX;
            if (Math.abs(delta) > 6) this.hasMoved = true;
            this.track.scrollLeft = this.startScroll - delta;
        }

        handlePointerUp(event) {
            if (!this.isDragging || event.pointerId !== this.pointerId) return;

            this.isDragging = false;
            this.pointerId = null;
            this.track.classList.remove("is-dragging");
            this.track.removeEventListener("pointermove", this.onPointerMove);
            this.track.removeEventListener("pointerup", this.onPointerUp);
            this.track.removeEventListener("pointercancel", this.onPointerUp);

            this.snapToNearest();
            this.startAutoplay();
        }

        handleClickCapture(event) {
            if (!this.hasMoved) return;
            event.preventDefault();
            event.stopPropagation();
            this.hasMoved = false;
        }

        handleBlockSelect(event) {
            const slide = event.target?.closest?.("[data-reviews-slide]");
            if (!slide || !this.contains(slide)) return;

            this.stopAutoplay();
            const index = this.slides.indexOf(slide);
            if (index >= 0) this.goTo(Math.min(index, this.maxIndex()));
        }
    }

    customElements.define("pdp-reviews-carousel", PdpReviewsCarousel);
}
